/**
 * <s-header-search> — Instant suggestions dropdown for the desktop header search bar.
 *
 * Typing queries Shopify's predictive search (prefix matching, so "prot" finds
 * "Protein") and lists matching products and collections right under the bar.
 * Implements the WAI-ARIA combobox pattern: arrow keys move through options,
 * Enter opens the active option (or submits the full search), Escape closes.
 * Without JavaScript the plain GET form still goes to the search page.
 *
 * Required child markup:
 *   input[role="combobox"]            — header search input.
 *   [data-hs-panel]                   — dropdown panel (hidden by default).
 *   [data-hs-products] / [data-hs-collections] / [data-hs-empty] / [data-hs-view-all]
 *   template[data-hs-product-tpl] / template[data-hs-collection-tpl]
 * Data attributes on host: data-url (predictive search route), data-search-url,
 *   data-currency, data-label-empty, data-label-view-all (both use __TERMS__).
 */
class HeaderSearch extends HTMLElement {
  #ac;
  #fetchAc;
  #timer = null;
  #input;
  #panel;
  #active = -1;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#input = this.querySelector('input[role="combobox"]');
    this.#panel = this.querySelector('[data-hs-panel]');
    if (!this.#input || !this.#panel) return;

    this.#input.addEventListener('input', () => {
      clearTimeout(this.#timer);
      const q = this.#input.value.trim();
      if (q.length < 2) {
        this.#close();
        return;
      }
      this.#timer = setTimeout(() => this.#search(q), 200);
    }, opts);

    this.#input.addEventListener('focus', () => {
      if (this.#input.value.trim().length >= 2 && this.#options.length) this.#open();
    }, opts);

    this.#input.addEventListener('keydown', (e) => this.#onKeydown(e), opts);

    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.#close();
    }, opts);

    // Keep focus in the input while clicking inside the panel; links still navigate.
    this.#panel.addEventListener('mousedown', (e) => e.preventDefault(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fetchAc?.abort();
    clearTimeout(this.#timer);
  }

  get #options() {
    return [...this.#panel.querySelectorAll('[role="option"]:not([hidden])')];
  }

  async #search(q) {
    this.#fetchAc?.abort();
    this.#fetchAc = new AbortController();
    const url = new URL(this.dataset.url + '.json', window.location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,variants.title,variants.sku,tag');
    try {
      const res = await fetch(url, { signal: this.#fetchAc.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(String(res.status));
      const json = await res.json();
      if (this.#input.value.trim() !== q) return;
      this.#render(q, json?.resources?.results || {});
    } catch (err) {
      if (err.name !== 'AbortError') this.#close();
    }
  }

  #money(value) {
    const amount = Number.parseFloat(value);
    if (Number.isNaN(amount)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: this.dataset.currency || 'AUD' }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }

  #render(q, results) {
    const products = results.products || [];
    const collections = results.collections || [];
    const productList = this.querySelector('[data-hs-products]');
    const collectionList = this.querySelector('[data-hs-collections]');
    const productTpl = this.querySelector('template[data-hs-product-tpl]');
    const collectionTpl = this.querySelector('template[data-hs-collection-tpl]');
    let index = 0;

    productList.replaceChildren();
    for (const p of products) {
      const node = productTpl.content.firstElementChild.cloneNode(true);
      node.id = `hs-option-${index++}`;
      node.href = p.url;
      node.querySelector('[data-hs-title]').textContent = p.title;
      const img = node.querySelector('img');
      if (p.image || p.featured_image?.url) {
        img.src = p.featured_image?.url || p.image;
        img.alt = '';
      } else {
        img.remove();
      }
      node.querySelector('[data-hs-price]').textContent = this.#money(p.price);
      const was = node.querySelector('[data-hs-was]');
      const compare = Number.parseFloat(p.compare_at_price_max);
      if (compare > Number.parseFloat(p.price)) {
        was.textContent = this.#money(compare);
      } else {
        was.remove();
      }
      productList.append(node);
    }
    productList.closest('[data-hs-group]').hidden = products.length === 0;

    collectionList.replaceChildren();
    for (const c of collections) {
      const node = collectionTpl.content.firstElementChild.cloneNode(true);
      node.id = `hs-option-${index++}`;
      node.href = c.url;
      node.textContent = c.title;
      collectionList.append(node);
    }
    collectionList.closest('[data-hs-group]').hidden = collections.length === 0;

    const empty = this.querySelector('[data-hs-empty]');
    empty.hidden = products.length + collections.length > 0;
    empty.textContent = (this.dataset.labelEmpty || '').replace('__TERMS__', q);

    const viewAll = this.querySelector('[data-hs-view-all]');
    viewAll.id = `hs-option-${index}`;
    viewAll.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(q)}`;
    viewAll.textContent = (this.dataset.labelViewAll || '').replace('__TERMS__', q);

    this.#setActive(-1);
    this.#open();
  }

  #open() {
    this.#panel.hidden = false;
    this.#input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.#panel.hidden = true;
    this.#input.setAttribute('aria-expanded', 'false');
    this.#setActive(-1);
  }

  #setActive(i) {
    const options = this.#options;
    options.forEach((o) => o.setAttribute('aria-selected', 'false'));
    this.#active = i;
    if (i >= 0 && options[i]) {
      options[i].setAttribute('aria-selected', 'true');
      options[i].scrollIntoView({ block: 'nearest' });
      this.#input.setAttribute('aria-activedescendant', options[i].id);
    } else {
      this.#input.removeAttribute('aria-activedescendant');
    }
  }

  #onKeydown(e) {
    const options = this.#options;
    if (this.#panel.hidden || !options.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.#setActive((this.#active + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.#setActive(this.#active <= 0 ? options.length - 1 : this.#active - 1);
    } else if (e.key === 'Enter' && this.#active >= 0) {
      e.preventDefault();
      window.location.href = options[this.#active].href;
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.#close();
    }
  }
}

if (!customElements.get('s-header-search')) customElements.define('s-header-search', HeaderSearch);
